import React, { useContext, useState } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { Filters } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";
import { handlesGrid } from "Util/handlesGrid";
import { styled } from "@mui/material";
import { MemberContext } from "../../context";
import { useLoginSessionsActions } from "../Tabs/Details/useActions";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";
import { useAppState } from "Context/StateContext";

export const ColumnStatus = styled("div")(() => ({
  position: "relative",
  left: "-2px",
  "& > div": {
    width: "fit-content",
  },
}));

export default function LoginSessions() {
  const { t: translate } = useTranslation();
  const { member } = useContext(MemberContext);
  const { currentUser } = useAppState();

  let { userId, companyId } = member;

  if (!userId || !companyId) {
    return <span>{translate("noInformationToBeShown")}</span>;
  }

  const safeParse = (raw: unknown) => {
    if (!raw) return null;
    try {
      return typeof raw === "string" ? JSON.parse(raw) : (raw as any);
    } catch (error) {
      console.error("Failed to parse deviceInfo JSON:", error, { value: raw });
      return null;
    }
  };

  const [filters, setFilters] = useState<Filters>({} as Filters);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const { handleCurrentPage, handlePageSize } = handlesGrid({
    setPage,
    setPageSize,
  });

  const { getSessions, getSessionsCount } = useLoginSessionsActions(userId, companyId);

  const { data: sessions, isFetching } = getSessions(page, pageSize, filters);

  const { data: sessionCount } = getSessionsCount(filters);

  const handleFilter = (value: Filters) => {
    setPage(0);
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : ({} as Filters));
  };

  const columns: Column[] = [
    {
      name: "deviceInfo", // operating_system
      title: translate("operating_system"),
      formatComponent: (value) => {
        if (!value) return "-";
        const json = safeParse(value);

        return <span>{json?.os || "-"}</span>;
      },
    },
    {
      name: "deviceInfo", // application
      title: translate("application"),
      formatComponent: (value, row) => {
        if (!value) return "-";
        const json = safeParse(value);

        if (json?.browser) return <span>{json.browser}</span>;
        const requestApp = json?.request?.split('/')?.[0];

        if (requestApp) {
          if (requestApp === "okhttp") {
            if (row?.platform && row?.platform === "ops") return <span>Shipeezi OPS</span>;
            else if (!row?.platform) return <span>Shipeezi Sales</span>;
          }

          return <span>{requestApp}</span>;
        }



        return <span>{"-"}</span>;
      },
    },
    {
      name: "deviceInfo", // application version
      title: translate("version"),
      formatComponent: (value) => {
        if (!value) return "-";
        const json = safeParse(value);
        if (json?.version) return <span>{json.version}</span>;
        const requestVersion = json?.request?.split('/')?.[1]?.split(' ')?.[0];
        return <span>{requestVersion || '-'}</span>;
      },
    },
    {
      name: "platform",
      title: translate("shipeezi_platform"),
      formatComponent: (value) => {
        if (!value) return "-";

        switch (value) {
          case "web":
            return "Shipeezi web";
          case "ops":
            return "Operations";
          case "sales":
            return "Sales App";
          default:
            return value;
        }
      },
    },
    {
      name: "ipAddress",
      title: translate("ip_address"),
    },
    {
      name: "lastActivity",
      title: translate("last_active_date"),
      formatComponent: (value) => {
        if (!value) return "-";

        let text = "";
        const now = new Date();
        const date = new Date(value);

        const diffInMs = now.getTime() - date.getTime();
        const diffInSeconds = Math.floor(diffInMs / 1000);
        const minutes = Math.floor(diffInSeconds / 60);

        if (diffInSeconds < 60) text = "just now";
        else if (minutes < 60) text = `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
        else {
          text = formatDateWithUserPref(value, currentUser?.preferences ?? {}, "full");
        }

        return <span>{text}</span>;
      },
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (value) => {
        const backgroundColor = value === "Active" ? colors.success50 : colors.gray100;
        const color = value === "Active" ? colors.success500 : colors.gray700;

        let text = value === "Active" ? value : "Logged out";
        return (
          <ColumnStatus>
            <BadgeStatus
              value={text}
              useExternalColor
              backgroundColor={backgroundColor}
              textColor={color}
            />
          </ColumnStatus>
        );
      },
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleFilterBy"],
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("shipeezi_platform"),
          column: "platform",
          options: [
            { label: "Shipeezi web", value: "web" },
            { label: "Operations", value: "ops" },
            { label: "Sales App", value: "sales" },
          ],
        },
        {
          name: translate("status"),
          column: "status",
          options: [
            { label: "Active", value: "Active" },
            { label: "Logged out", value: "Inactive" },
          ],
        },
      ],
      onChange: handleFilter,
    },
  };

  return (
    <ShipeeziNewGrid
      loadingData={isFetching}
      data={sessions || []}
      totalItems={sessionCount || 0}
      columns={columns}
      showSelection={false}
      showSort={false}
      currentPage={page}
      pageSize={pageSize}
      pageSizeList={[10, 20, 40, 60]}
      onChangePageSize={handlePageSize}
      onNextPage={handleCurrentPage}
      onPreviousPage={handleCurrentPage}
      onGoToPage={handleCurrentPage}
      showFooterGrid
      positionActionsColumn="right"
      generalActions={generalActions}
      keyExtractor={(data) => data?.id?.toString() || data?.randomUuid}
      emptyData={translate("noInformationToBeShown")}
      alwaysShowFooter={true}
    />
  );
}
